import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js'
import { Token } from '@stripe/stripe-js'
import clsx from 'clsx'
import { useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { User } from '../../models/User'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { TextField } from '../common/fields/TextField'
import { WorkflowIconSection } from '../workflows/WorkflowIconSection'

type Props = {
    isOpen: boolean
    onClose: () => void
    onSuccess:
        | { action: 'create-source'; client: User | undefined; callback: (source: Source) => void }
        | { action: 'return-source-token'; callback: (token: Token) => void }
    onBack?: () => void
    forProOnly?: boolean
}

const stripeOptions = {
    style: {
        base: {
            color: '#2f3941',
            fontWeight: '100',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '16px',
            lineHeight: '1.5',
            '::placeholder': {
                color: '#999999',
            },
            border: '1px solid #757575',
        },
    },
    disableLink: true,
}

export function CardModal({ isOpen, onClose, onSuccess, onBack, forProOnly = false }: Props) {
    const [focusCardNumber, setFocusCardNumber] = useState(false)
    const [focusCardExpiry, setFocusCardExpiry] = useState(false)
    const [focusCardCvc, setFocusCardCvc] = useState(false)
    const { logAndDisplayError } = useErrorHandler()
    const [isSaving, setIsSaving] = useState(false)
    const [instantPayoutWarning, setInstantPayoutWarning] = useState(false)

    const [name, setName] = useState('')
    const [postalCode, setPostalCode] = useState('')
    const [allowDeposit, setAllowDeposit] = useState(false)
    const stripe = useStripe()
    const elements = useElements()

    const [stripeFieldCompletionStatus, setStripeFieldCompletionStatus] = useState({
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
    })

    const me = PSApplication.me()

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        if (!stripe || !elements) {
            return
        }

        const cardElement = elements.getElement(CardNumberElement)
        if (!cardElement) {
            return
        }

        const { error, token } = await stripe.createToken(cardElement, {
            name: name,
            address_zip: postalCode,
            address_country: 'US',
            currency: 'USD',
        })

        if (error || !token) {
            logAndDisplayError(error)
            return
        }

        if (token.card?.funding == 'credit' && allowDeposit) {
            setInstantPayoutWarning(true)
            return
        }

        if (onSuccess.action === 'return-source-token') {
            onSuccess.callback(token)
            return
        }

        const source: Source = new Source()
        source.token = token.id

        if (onSuccess.client) {
            source.owner = onSuccess.client
            source.cardScanned = false
            source.allowDeposit = false
            source.allowPayment = false
            source.type = me.sp ? 'debit' : 'credit'
            source.name = name
        }
        else {
            source.allowDeposit = allowDeposit
        }

        const result = await RecordFactory.getInstance().sendSource(source)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        onSuccess.callback(result.value)
    }

    return (
        <>
            <Modal
                title="Add a payment card"
                isOpen={isOpen}
                onClose={onClose}
                onNavigateBack={onBack}
                size="large"
                footer={
                    <Button
                        intent="primary"
                        onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                            setIsSaving(true)
                            await handleSubmit(e)
                            setIsSaving(false)
                        }}
                        isLoading={isSaving}
                        disabled={
                            !stripeFieldCompletionStatus.cardNumber ||
                            !stripeFieldCompletionStatus.cardExpiry ||
                            !stripeFieldCompletionStatus.cardCvc ||
                            !name ||
                            !postalCode
                        }
                    >
                        Save
                    </Button>
                }
            >
                <form className="mb-1 flex flex-col gap-8">
                    <WorkflowIconSection icon="bankCard.large" title="Card #">
                        <CardNumberElement
                            onFocus={() => setFocusCardNumber(true)}
                            onBlur={() => setFocusCardNumber(false)}
                            className={clsx(
                                'text-paragraph-md w-full cursor-pointer rounded-lg border border-gray-500 bg-white px-4 py-[12px] outline-0',
                                { 'hover:border-primary-700 hover:outline-1': !focusCardNumber },
                                { 'outline-primary-700 outline-2': focusCardNumber }
                            )}
                            onChange={(event) =>
                                setStripeFieldCompletionStatus((previous) => ({
                                    ...previous,
                                    cardNumber: event.complete,
                                }))
                            }
                            options={stripeOptions}
                        />
                    </WorkflowIconSection>

                    <WorkflowIconSection icon="calendar.large" title="Expiration date">
                        <CardExpiryElement
                            onFocus={() => setFocusCardExpiry(true)}
                            onBlur={() => setFocusCardExpiry(false)}
                            className={clsx(
                                'text-paragraph-md w-full cursor-pointer rounded-lg border border-gray-500 bg-white px-4 py-[12px] outline-0',
                                { 'hover:border-primary-700 hover:outline-1': !focusCardExpiry },
                                { 'outline-primary-700 outline-2': focusCardExpiry }
                            )}
                            onChange={(event) =>
                                setStripeFieldCompletionStatus((previous) => ({
                                    ...previous,
                                    cardExpiry: event.complete,
                                }))
                            }
                            options={stripeOptions}
                        />
                    </WorkflowIconSection>

                    <WorkflowIconSection icon="hash.large" title="CVC">
                        <CardCvcElement
                            onFocus={() => setFocusCardCvc(true)}
                            onBlur={() => setFocusCardCvc(false)}
                            className={clsx(
                                'text-paragraph-md w-full cursor-pointer rounded-lg border border-gray-500 bg-white px-4 py-[12px] outline-0',
                                { 'hover:border-primary-700 hover:outline-1': !focusCardCvc },
                                { 'outline-primary-700 outline-2': focusCardCvc }
                            )}
                            onChange={(event) =>
                                setStripeFieldCompletionStatus((previous) => ({
                                    ...previous,
                                    cardCvc: event.complete,
                                }))
                            }
                            options={stripeOptions}
                        />
                    </WorkflowIconSection>

                    <WorkflowIconSection icon="user.large" title="Name on card">
                        <TextField
                            size="medium"
                            placeholder="John Doe"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </WorkflowIconSection>

                    <WorkflowIconSection icon="globe2.large" title="Zipcode">
                        <TextField
                            size="medium"
                            placeholder="90210"
                            mask="00000"
                            onChange={(e) => setPostalCode(e.target.value)}
                        />
                    </WorkflowIconSection>
                    {forProOnly && (
                        <WorkflowIconSection
                            title="Accept instant payouts"
                            icon="calendarDollar.large"
                            hasHoverEffect
                            description="Instant payouts are only for debit cards."
                            secondaryView={
                                <Checkbox
                                    isChecked={allowDeposit}
                                    className="ml-3"
                                    onChange={setAllowDeposit}
                                />
                            }
                        ></WorkflowIconSection>
                    )}
                </form>
            </Modal>

            <Modal
                isOpen={instantPayoutWarning}
                onClose={() => setInstantPayoutWarning(false)}
                size="small"
                title="Warning"
                footer={
                    <Button intent="primary" onClick={() => setInstantPayoutWarning(false)}>
                        Ok
                    </Button>
                }
            >
                <div className="px-4 py-8">Instant payouts are only allowed for debit cards</div>
            </Modal>
        </>
    )
}
